import { useEffect, useState } from "react";
import { PanelLoading } from "../../components/Loading";
import { useTranslation } from "react-i18next";
import { auditApi, type AuditAction, type AuditLogRow } from "../../api/audit";
import type { ApiError } from "../../api/client";
import { useToast } from "../../components/Toast";
import { Pagination } from "../../components/Pagination";
import { ExportButton } from "../../components/ExportButton";
import { Select } from "../../components/Select";

const ACTIONS: AuditAction[] = [
  "CREATE", "UPDATE", "DELETE",
  "LOGIN", "LOGOUT", "LOGIN_FAILED",
  "INVITE", "APPROVE", "REJECT",
  "PASSWORD_RESET_REQUEST", "PASSWORD_RESET_COMPLETE",
  "TOKEN_REFRESH", "ACCOUNT_LOCKED", "ACCOUNT_UNLOCKED",
];

const ENTITY_TYPES = [
  "Company", "CompanyAddress", "User", "Invitation", "CompanySalesAssignment", "CompanyOtkupAssignment",
  "CustomerAlias", "Product", "ProductCategory", "Material", "MaterialCategory",
  "Article", "ArticleCategory",
  "Contract", "ContractVersion",
  "Order", "OrderImport", "OrderImportItem", "Forecast", "Complaint", "KpiTarget", "ErpOutbox",
  "ProcurementContract", "ProcurementContractAmendment", "ProcurementContractDocument",
  "Delivery", "DeliveryDocument", "ReceivingLot", "LotUsage", "SupplierSettlement",
  "Trip", "TripItem", "TripWeighing", "Carrier", "Vehicle", "Driver",
];

const PAGE_SIZE = 25;

export default function AdminAuditLogPage() {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const [rows, setRows] = useState<AuditLogRow[] | null>(null);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [actionFilter, setActionFilter] = useState<AuditAction | "">("");
  const [entityFilter, setEntityFilter] = useState<string>("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [openRow, setOpenRow] = useState<AuditLogRow | null>(null);

  async function load() {
    try {
      const result = await auditApi.search({
        action: actionFilter || undefined,
        entityType: entityFilter || undefined,
        from: from ? new Date(from).toISOString() : undefined,
        to: to ? new Date(to).toISOString() : undefined,
        page,
        size: PAGE_SIZE,
      });
      setRows(result.content);
      setTotalPages(result.totalPages);
    } catch (err) {
      toast.error((err as ApiError).message);
    }
  }

  useEffect(() => { setPage(0); }, [actionFilter, entityFilter, from, to]);
  useEffect(() => { void load(); }, [actionFilter, entityFilter, from, to, page]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h1 style={{ margin: 0, fontSize: 24, color: "var(--brand-ink)" }}>{t("admin.auditLog.title")}</h1>
        <div style={{ display: "flex", gap: 8 }}>
          <ExportButton
            path={`/exports/audit-log.csv${buildAuditQuery({ actionFilter, entityFilter, from, to })}`}
            label={t("admin.auditLog.exportCsv")}
            filename="audit-log.csv"
          />
          <button type="button" onClick={load} style={secondary}>{t("admin.auditLog.refresh")}</button>
        </div>
      </header>

      <div style={filterRow}>
        <Field label={t("admin.auditLog.field.action")}>
          <Select<AuditAction | "">
            value={actionFilter}
            onChange={setActionFilter}
            ariaLabel={t("admin.auditLog.field.action")}
            options={[
              { value: "", label: t("admin.auditLog.all") },
              ...ACTIONS.map((a) => ({ value: a, label: a })),
            ]}
          />
        </Field>
        <Field label={t("admin.auditLog.field.entityType")}>
          <Select<string>
            value={entityFilter}
            onChange={setEntityFilter}
            ariaLabel={t("admin.auditLog.field.entityType")}
            options={[
              { value: "", label: t("admin.auditLog.all") },
              ...ENTITY_TYPES.map((tp) => ({ value: tp, label: tp })),
            ]}
          />
        </Field>
        <Field label={t("admin.auditLog.field.from")}>
          <input type="datetime-local" value={from} onChange={(e) => setFrom(e.target.value)} style={input} />
        </Field>
        <Field label={t("admin.auditLog.field.to")}>
          <input type="datetime-local" value={to} onChange={(e) => setTo(e.target.value)} style={input} />
        </Field>
      </div>

      {!rows ? (
        <PanelLoading text={t("common.loading")} />
      ) : rows.length === 0 ? (
        <p style={{ color: "var(--ink-3)" }}>{t("admin.auditLog.empty")}</p>
      ) : (
        <table style={tbl}>
          <thead>
            <tr>
              <th style={th}>{t("admin.auditLog.col.when")}</th>
              <th style={th}>{t("admin.auditLog.col.actor")}</th>
              <th style={th}>{t("admin.auditLog.col.action")}</th>
              <th style={th}>{t("admin.auditLog.col.entity")}</th>
              <th style={th}>{t("admin.auditLog.col.ip")}</th>
              <th style={th}>{t("admin.auditLog.col.request")}</th>
              <th style={{ ...th, textAlign: "right" }}>{t("common.actions", "Actions")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td style={td}>{new Date(r.createdAt).toLocaleString(i18n.language)}</td>
                <td style={td}>{r.actorName ?? <span style={{ color: "var(--ink-4)" }}>{t("admin.auditLog.system")}</span>}</td>
                <td style={td}><ActionBadge action={r.action} /></td>
                <td style={td}>
                  <code style={{ fontSize: 11 }}>{r.entityType}</code>
                  {r.entityId && <small style={{ color: "var(--ink-4)", display: "block" }}>{r.entityId.slice(0, 8)}…</small>}
                </td>
                <td style={td}>{r.ipAddress ?? "—"}</td>
                <td style={td}>{r.requestId ? <code style={{ fontSize: 10 }}>{r.requestId.slice(0, 8)}</code> : "—"}</td>
                <td style={td}>
                  {(r.beforeData || r.afterData) && (
                    <button type="button" onClick={() => setOpenRow(r)} style={linkBtn}>{t("admin.auditLog.diff")}</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <Pagination page={page} totalPages={totalPages} onChange={setPage} />

      {openRow && <DiffModal row={openRow} onClose={() => setOpenRow(null)} />}
    </div>
  );
}

function DiffModal({ row, onClose }: { row: AuditLogRow; onClose: () => void }) {
  const { t, i18n } = useTranslation();
  return (
    <div onClick={onClose} style={overlay}>
      <div onClick={(e) => e.stopPropagation()} style={dialog}>
        <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h2 style={{ margin: 0, fontSize: 18, color: "var(--brand-ink)" }}>
            <code style={{ fontSize: 14 }}>{row.entityType}</code> · <ActionBadge action={row.action} />
          </h2>
          <button type="button" onClick={onClose} aria-label={t("action.close", { defaultValue: "Close" })} style={{ all: "unset", cursor: "pointer", color: "var(--ink-3)", fontSize: 20 }}>×</button>
        </header>
        <small style={{ color: "var(--ink-3)" }}>
          {new Date(row.createdAt).toLocaleString(i18n.language)} · {row.actorName ?? t("admin.auditLog.system")}
        </small>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <DataPanel title={t("admin.auditLog.before")} json={row.beforeData} tone="neutral" />
          <DataPanel title={t("admin.auditLog.after")} json={row.afterData} tone="positive" />
        </div>
      </div>
    </div>
  );
}

function DataPanel({ title, json, tone }: { title: string; json: string | null; tone: "neutral" | "positive" }) {
  let pretty = "—";
  if (json) {
    try {
      pretty = JSON.stringify(JSON.parse(json), null, 2);
    } catch {
      pretty = json;
    }
  }
  const bg = tone === "positive" ? "var(--ok-bg)" : "var(--surface-muted)";
  return (
    <div>
      <h3 style={subTitle}>{title}</h3>
      <pre style={{ ...pre, background: bg }}>{pretty}</pre>
    </div>
  );
}

function ActionBadge({ action }: { action: AuditAction }) {
  const palette: Record<string, { bg: string; fg: string }> = {
    CREATE: { bg: "var(--ok-bg)", fg: "var(--ok)" },
    UPDATE: { bg: "var(--info-bg)", fg: "var(--info)" },
    DELETE: { bg: "var(--err-bg)", fg: "var(--err)" },
    LOGIN: { bg: "var(--tone-cyan-bg)", fg: "var(--tone-cyan)" },
    LOGOUT: { bg: "var(--surface-muted)", fg: "var(--ink-3)" },
    LOGIN_FAILED: { bg: "var(--warn-bg)", fg: "var(--warn)" },
    INVITE: { bg: "var(--tone-violet-bg)", fg: "var(--tone-violet)" },
    APPROVE: { bg: "var(--ok-bg)", fg: "var(--ok)" },
    REJECT: { bg: "var(--err-bg)", fg: "var(--err)" },
    PASSWORD_RESET_REQUEST: { bg: "var(--warn-bg)", fg: "var(--warn)" },
    PASSWORD_RESET_COMPLETE: { bg: "var(--ok-bg)", fg: "var(--ok)" },
    TOKEN_REFRESH: { bg: "var(--surface-muted)", fg: "var(--ink-3)" },
    ACCOUNT_LOCKED: { bg: "var(--err-bg)", fg: "var(--err)" },
    ACCOUNT_UNLOCKED: { bg: "var(--ok-bg)", fg: "var(--ok)" },
  };
  const c = palette[action] ?? { bg: "var(--surface-muted)", fg: "var(--ink-3)" };
  return (
    <span style={{ background: c.bg, color: c.fg, padding: "2px 8px", borderRadius: 999, fontSize: 11, fontWeight: 600 }}>
      {action}
    </span>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 12, flex: 1, minWidth: 140 }}>
      <span style={{ color: "var(--ink-3)" }}>{label}</span>
      {children}
    </label>
  );
}

const filterRow: React.CSSProperties = { display: "flex", gap: 12, flexWrap: "wrap" };
function buildAuditQuery(f: { actionFilter: string; entityFilter: string; from: string; to: string }): string {
  const params = new URLSearchParams();
  if (f.actionFilter) params.set("action", f.actionFilter);
  if (f.entityFilter) params.set("entityType", f.entityFilter);
  if (f.from) params.set("from", new Date(f.from).toISOString());
  if (f.to) params.set("to", new Date(f.to).toISOString());
  const q = params.toString();
  return q ? `?${q}` : "";
}

const tbl: React.CSSProperties = { width: "100%", borderCollapse: "collapse", fontSize: 12, background: "var(--surface)", borderRadius: 8, overflow: "hidden", boxShadow: "var(--shadow-1)" };
const th: React.CSSProperties = { textAlign: "left", padding: "8px 10px", background: "var(--surface-2)", color: "var(--ink-3)", fontWeight: 600, borderBottom: "1px solid var(--border)" };
const td: React.CSSProperties = { padding: "8px 10px", borderBottom: "1px solid var(--border)", verticalAlign: "top" };
const input: React.CSSProperties = { padding: "8px 10px", borderRadius: 6, border: "1px solid var(--border-strong)", fontSize: 13 };
const linkBtn: React.CSSProperties = { background: "transparent", border: "none", color: "var(--brand-ink)", cursor: "pointer", fontSize: 12 };
const secondary: React.CSSProperties = { padding: "8px 14px", background: "var(--surface)", border: "1px solid var(--border-strong)", borderRadius: 6, fontSize: 14, cursor: "pointer" };
const overlay: React.CSSProperties = { position: "fixed", inset: 0, background: "var(--overlay)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50, padding: 16 };
const dialog: React.CSSProperties = { width: "100%", maxWidth: 900, background: "var(--surface)", padding: 24, borderRadius: 12, boxShadow: "var(--shadow-2)", display: "flex", flexDirection: "column", gap: 14, maxHeight: "90vh", overflowY: "auto" };
const subTitle: React.CSSProperties = { margin: "0 0 6px", fontSize: 11, color: "var(--ink-3)", textTransform: "uppercase", letterSpacing: 0.5 };
const pre: React.CSSProperties = { margin: 0, padding: 12, fontSize: 11, fontFamily: "ui-monospace, monospace", borderRadius: 6, overflow: "auto", maxHeight: 400 };
